import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import type { TaskInstance } from 'ember-concurrency';
import { t } from 'ember-intl';
import SettingsPmsIntegrationSeasonalRoomHierarchyModal from 'lumen/components/settings/pms-integration/seasonal-room-hierarchy-modal';
import type { RecommendationStrategy } from 'lumen/models';
import { sortSeasonalStrategies } from 'lumen/utils/pricing-assistant/recommendation-settings';

interface SettingsPmsIntegrationSeasonalRoomHierarchySectionSignature {
  Args: {
    offsetTypeLabel: string;
  };
}

export default class SettingsPmsIntegrationSeasonalRoomHierarchySection extends Component<SettingsPmsIntegrationSeasonalRoomHierarchySectionSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  @tracked isModalOpen = false;
  @tracked seasonalStrategies: RecommendationStrategy[] = [];
  @tracked strategiesTask: TaskInstance<
    RecommendationStrategy[] | null
  > | null = null;

  get isLoading(): boolean {
    return this.strategiesTask?.isRunning ?? false;
  }

  get isReadOnly(): boolean {
    return this.access.isPricingAssistantPmsSettingsActionOrInputDisabled;
  }

  @action
  closeModal(): void {
    this.isModalOpen = false;
  }

  @action
  async deleteSeasonalStrategy(
    seasonalStrategy: RecommendationStrategy,
  ): Promise<void> {
    const isConfirmed = await this.alerting.showWarningAlert({
      title: this.intl.t(
        'pricing_assistant.recommendations.delete_seasonal_strategy',
      ),
      text: this.intl.t(
        'pricing_assistant.recommendations.delete_seasonal_strategy_text',
        { strategy: seasonalStrategy.name },
      ),
      dangerMode: true,
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: { text: this.intl.t('general.delete') },
      },
    });

    if (!isConfirmed) {
      return;
    }

    try {
      await seasonalStrategy.destroyRecord();
      this.seasonalStrategies = this.seasonalStrategies.filter(
        (strategy) => strategy !== seasonalStrategy,
      );
    } catch {
      // Un-delete the locally-flagged record, then surface the failure.
      seasonalStrategy.rollbackAttributes();
      await this.alerting.showErrorAlert();
    }
  }

  @action
  onCreateSeasonalStrategy(): void {
    // TODO(INDU-1677): open the seasonal room-hierarchy editor.
  }

  @action
  async openModal(): Promise<void> {
    this.isModalOpen = true;
    this.strategiesTask =
      this.pricingAssistantDataRequestService.recommendationsStrategiesTask.perform(
        'room_offset_hierarchy',
      );
    this.seasonalStrategies = sortSeasonalStrategies(
      (await this.strategiesTask) ?? [],
    );
  }

  <template>
    <div class="divider-line"></div>

    <div
      class="flex flex-row items-center justify-between"
      data-test-id="seasonal-room-hierarchy-section"
    >
      <p class="text-200--bold">{{t
          "pricing_assistant.recommendations.price_range.subtitle.seasonal_strategies"
        }}</p>

      <PrismButton
        @buttonType="secondary"
        @label={{if this.isReadOnly (t "general.view") (t "general.edit")}}
        @onClick={{this.openModal}}
      />
    </div>

    {{#if this.isModalOpen}}
      <SettingsPmsIntegrationSeasonalRoomHierarchyModal
        @isLoading={{this.isLoading}}
        @isReadOnly={{this.isReadOnly}}
        @offsetTypeLabel={{@offsetTypeLabel}}
        @onClose={{this.closeModal}}
        @onCreateSeasonalStrategy={{this.onCreateSeasonalStrategy}}
        @onDeleteSeasonalStrategy={{this.deleteSeasonalStrategy}}
        @seasonalStrategies={{this.seasonalStrategies}}
      />
    {{/if}}
  </template>
}
